import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import {
  PortalType,
  WhitelistItem,
  WhitelistService
} from '../../services/whitelist.service';

interface WhitelistCategory {
  name: string;
  items: WhitelistItem[];
}

@Component({
  selector: 'app-whitelist-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './whitelist-page.component.html',
  styleUrl: './whitelist-page.component.css'
})
export class WhitelistPageComponent implements OnInit {

  private readonly route = inject(ActivatedRoute);
  private readonly whitelistService = inject(WhitelistService);
  private readonly cdr = inject(ChangeDetectorRef);

  portalType: PortalType = 'Crew';

  items: WhitelistItem[] = [];
  categories: WhitelistCategory[] = [];

  loading = true;
  error = false;

  ngOnInit(): void {
    this.portalType =
      this.route.snapshot.data['portalType'] as PortalType;

    this.loadItems();
  }

  private loadItems(): void {
    this.loading = true;
    this.error = false;

    this.whitelistService
      .getForPortal(this.portalType)
      .subscribe({
        next: items => {
          this.items = items;

          this.buildCategories();

          this.loading = false;

          this.cdr.detectChanges();
        },

        error: error => {
          console.error(
            'Failed to load portal resources:',
            error
          );

          this.items = [];
          this.categories = [];

          this.error = true;
          this.loading = false;

          this.cdr.detectChanges();
        }
      });
  }

  private buildCategories(): void {
    const categoryMap =
      new Map<string, WhitelistItem[]>();

    for (const item of this.items) {
      const category =
        item.category || 'Resources';

      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }

      categoryMap.get(category)!.push(item);
    }

    this.categories =
      Array.from(categoryMap.entries())
        .map(([name, items]) => ({
          name,
          items: items.sort(
            (a, b) =>
              a.displayOrder - b.displayOrder
          )
        }));
  }

  openItem(item: WhitelistItem): void {

    // Protected PDF
    if (item.documentId) {
      this.openDocument(item);
      return;
    }

    // External link
    if (item.url?.trim()) {
      window.open(
        item.url,
        '_blank',
        'noopener,noreferrer'
      );

      return;
    }

    // No URL and no document = placeholder.
  }

  private openDocument(
    item: WhitelistItem
  ): void {

    this.whitelistService
      .getDocument(item.id)
      .subscribe({
        next: blob => {
          const objectUrl =
            URL.createObjectURL(blob);

          window.open(
            objectUrl,
            '_blank',
            'noopener,noreferrer'
          );

          setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
          }, 60000);
        },

        error: error => {
          console.error(
            `Failed to open document "${item.name}":`,
            error
          );
        }
      });
  }

  isPlaceholder(
    item: WhitelistItem
  ): boolean {

    return (
      !item.documentId &&
      !item.url?.trim()
    );
  }

  isDocument(
    item: WhitelistItem
  ): boolean {

    return !!item.documentId;
  }
}